import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { DomainConfigComponent } from '@app/domain-config.component';
import { Module } from '@models/module';
import {
  FALLBACK_ROUTE,
  ModuleName,
  ModuleSection,
  ModuleEntry,
  isOpenable,
  isShown,
  pickDefaultRoute,
  visibleChildren,
  visibleSections
} from './module-access.logic';
import { FlujosPorModulo, seccionesDeSuite } from './suite-menu.logic';

export { FALLBACK_ROUTE } from './module-access.logic';
export type { ModuleName } from './module-access.logic';

/**
 * Único lugar que decide qué módulos tiene la cuenta y dónde aterriza el usuario.
 *
 * Antes esta lógica estaba duplicada en `intro` y en `header-nav` —y en
 * `header-nav` con un `splice()` dentro de un `forEach()`, que se salta un módulo
 * por cada uno que quita— y las rutas no la aplicaban en absoluto.
 *
 * Las reglas viven en `module-access.logic.ts`, sin Angular, para poder probarlas
 * sin navegador. Acá solo se resuelven las entradas: los módulos del domain
 * config, los permisos del perfil y las rutas declaradas.
 */
@Injectable({ providedIn: 'root' })
export class ModuleAccessService {

  /**
   * Módulos cuya disponibilidad NO se puede resolver con el domain config.
   *
   * `clm` empieza oculto a propósito. Su flag `firma` está en "true" en los 12
   * tenants de QA, pero /trackvision/lexgosign/status responde `enabled: false`
   * en demotest: el flag dice "está en tu plan" y el status dice "está
   * activado", y no son lo mismo. Mostrar la tarjeta con el flag hacía que el
   * usuario la pulsara y `LexgosignEnabledGuard` lo expulsara. Oculto hasta que
   * el status lo confirme: es peor una entrada que rebota que una que tarda.
   */
  private noDisponibles = new Set<string>(['clm', 'sign']);

  /** Avisa a quien pinte la lista de módulos de que la disponibilidad cambió. */
  readonly changes$ = new Subject<void>();

  /** La resuelve quien pueda consultarla; hoy `header-nav`, que ya pregunta. */
  setModuleAvailable(name: string, disponible: boolean) {
    const antes = !this.noDisponibles.has(name);
    if (antes === disponible) return;
    disponible ? this.noDisponibles.delete(name) : this.noDisponibles.add(name);
    this.changes$.next();
  }

  constructor(
    private domainConfig: DomainConfigComponent,
    private permissions: NgxPermissionsService,
    private router: Router
  ) {}

  /** Los módulos de la suite con su estado ya resuelto desde el domain config. */
  getModules(): Module[] {
    return this.domainConfig.getModuleVisible()
      .filter((module) => !this.noDisponibles.has(module.name));
  }

  /**
   * Los que se muestran: con permiso y, o visibles, o deshabilitados. Los
   * deshabilitados se pintan en gris a propósito, como teaser del plan.
   */
  getLauncherModules(): Module[] {
    return this.getModules().filter((module) => isShown(module, this.tienePermiso));
  }

  /**
   * Secciones del sidebar de la suite: flag del dominio, permiso del perfil y,
   * en los módulos cuyos hijos son flujos, que el cliente tenga alguno.
   * No usa `noDisponibles`: ahí `clm` es la tarjeta de firma, no Contratos CLM.
   * Ocultar no protege: los guards de ruta y el backend siguen validando.
   */
  getSuiteSections(firma: boolean, flujos: FlujosPorModulo): ModuleSection[] {
    return seccionesDeSuite(this.domainConfig.getSuiteModules(firma), this.tienePermiso, flujos);
  }

  /** Subitems del catálogo que el perfil puede ver. Los flujos no pasan por acá. */
  getSuiteChildren(module: ModuleEntry): ModuleEntry[] {
    return visibleChildren(module, this.tienePermiso);
  }

  /**
   * Los módulos visibles agrupados por sección (PRINCIPAL · CONTRATO · ADMIN de
   * la lámina 22), para el sidebar de dos niveles. Secciones vacías se omiten.
   */
  getSections(): ModuleSection[] {
    return visibleSections(this.getModules(), this.tienePermiso);
  }

  /** Subitems visibles de un módulo (FIRMA → Mis docs / Estatus). */
  getChildren(module: ModuleEntry): ModuleEntry[] {
    return visibleChildren(module, this.tienePermiso);
  }

  /** Los que el usuario puede ABRIR. */
  getNavigableModules(): Module[] {
    return this.getModules().filter((module) => isOpenable(module, this.tienePermiso));
  }

  /** true solo si el usuario puede entrar al módulo. Un módulo en gris no se navega. */
  canNavigate(name: ModuleName): boolean {
    const module = this.getModules().find((candidate) => candidate.name === name);
    // Ruta sin ficha de módulo: no la gateamos, que la resuelvan sus permisos.
    if (!module) return true;
    return isOpenable(module, this.tienePermiso);
  }

  /** Link del launcher para un módulo, para redirigir sin hardcodear rutas. */
  getModuleLink(name: ModuleName): string | undefined {
    return this.getModules().find((candidate) => candidate.name === name)?.link;
  }

  /** Dónde entra este usuario. `null` si no hay destino seguro. */
  getDefaultRoute(): string | null {
    return pickDefaultRoute({
      modules: this.getModules(),
      tienePermiso: this.tienePermiso,
      routes: this.router.config,
      configLoaded: this.isDomainConfigLoaded()
    });
  }

  /** Destino de aterrizaje, con el fallback a la pantalla intermedia. */
  getDefaultRouteOrFallback(): string {
    return this.getDefaultRoute() ?? FALLBACK_ROUTE;
  }

  private tienePermiso = (permiso: string): boolean => !!this.permissions.getPermission(permiso);

  /**
   * `DomainConfigComponent.id` se asigna solo cuando la config del dominio llegó
   * de verdad; el `catchError` de `load()` no lo toca. Es la única señal que
   * distingue "este cliente tiene Flows" de "no pudimos preguntar".
   */
  private isDomainConfigLoaded(): boolean {
    return this.domainConfig.id !== null && this.domainConfig.id !== undefined;
  }
}
